/**
 * Created by neo on 12.04.22.
 */
import { makeAutoObservable, toJS } from 'mobx';
import { v4 as UUID } from 'uuid';
import { LocalizedEntity, createLocalizedEntity } from '../../../LocalizedEntity';
import { HttpBackend } from '../../../../Services/Http/HttpBackend';
import { MindfulnessProgramTemplatePhase, createMindfulnessProgramTemplatePhase } from './MindfulnessProgramTemplatePhase';
import { ProgramTemplateResult, createProgramTemplateResult } from '../../ProgramTemplateResult';
import { Quote, createQuote } from '../../Quote';
import { MicroCycle, createMicroCycle } from '../../Program/MicroCycle';
import { ConditionalMedia, createConditionalMedia } from '../../SuperMacro/ConditionalMedia';
import { Media } from '../../../Media/Media';
import { MindfulnessProgramTemplateQueryRequest } from './MindfulnessProgramTemplateQueryRequest';
import { ProgramSchedule } from '../../ProgramSchedule';
import CacheService from '../../../../Services/CacheService';
import { HOURS_1 } from '../../../../Utils/Constants';
import { TimeOfDay } from '../../../TimeOfDay';
import { ProgramActiveResponse, createProgramActiveResponse } from '../../ProgramActiveResponse';
import { TimesOfDay } from '../../TimesOfDay';
import { ScheduleTimeRange, createScheduleTimeRange } from '../../ScheduleTimeRange';

export type CreateMindfulnessProgramRequest = {
  programTemplateId: string;
  tags: string[];
  schedule: Partial<ProgramSchedule>;
};

export type MindfulnessProgramTemplate = LocalizedEntity & {
  templateName: string;
  type: string;
  tags: string[];
  images: ConditionalMedia[];
  phases: MindfulnessProgramTemplatePhase[];
  expectedResults: ProgramTemplateResult[];
  quotes: Quote[];
  defaultTimesOfDay: TimesOfDay;
  readonly minDaysPerWeek: number;
  readonly maxDaysPerWeek: number;
  readonly weeksCount: number;
  readonly minDurationInMinutes: number | undefined;
  readonly maxDurationInMinutes: number | undefined;
  readonly lastPhase: MindfulnessProgramTemplatePhase | undefined;
  readonly totalUnits: number;
  readonly totalUnitsWithWeeks: number;
  readonly timesOfDay: TimeOfDay[];
  save(): Promise<MindfulnessProgramTemplate>;
  delete(): Promise<MindfulnessProgramTemplate>;
  copy(): MindfulnessProgramTemplate;
  getImage(tags?: string[]): Media | undefined;
  create(params?: Partial<CreateMindfulnessProgramRequest>): Promise<MicroCycle>;
};

export function createMindfulnessProgramTemplate(json?: Partial<MindfulnessProgramTemplate>): MindfulnessProgramTemplate {
  const base = createLocalizedEntity(json as any);
  const template: MindfulnessProgramTemplate = makeAutoObservable({
    ...base,
    templateName: json?.templateName ?? '',
    type: json?.type ?? '',
    tags: json?.tags ?? [],
    images: (json?.images ?? []).map((m: any) => createConditionalMedia(m)),
    phases: (json?.phases ?? []).map((p: any) => createMindfulnessProgramTemplatePhase(p)),
    expectedResults: (json?.expectedResults ?? []).map((r: any) => createProgramTemplateResult(r)),
    quotes: (json?.quotes ?? []).map((r: any) => createQuote(r)),
    defaultTimesOfDay: Object.entries((json?.defaultTimesOfDay as any) ?? ({} as TimesOfDay))
      .map(([key, value]) => [key, createScheduleTimeRange(value as any)])
      .reduce((obj, [key, value]) => Object.assign(obj, { [key as TimeOfDay]: value }), {} as TimesOfDay),

    get minDaysPerWeek(): number {
      return this.phases.reduce((min, phase) => Math.min(min, phase.days.length), Number.MAX_SAFE_INTEGER);
    },

    get maxDaysPerWeek(): number {
      return this.phases.reduce((min, meso) => Math.max(min, meso.days.length), 0);
    },

    get weeksCount(): number {
      return this.phases.reduce((min, phase) => min + phase.cycles, 0);
    },

    get minDurationInMinutes(): number | undefined {
      return this.phases.reduce(
        (agg, cycle) =>
          cycle.minDurationInMinutes ? Math.min(cycle.minDurationInMinutes, agg ?? Number.MAX_SAFE_INTEGER) : agg,
        undefined as number | undefined,
      );
    },

    get maxDurationInMinutes(): number | undefined {
      return this.phases.reduce(
        (agg, cycle) =>
          cycle.maxDurationInMinutes ? Math.max(cycle.maxDurationInMinutes, agg ?? Number.MIN_SAFE_INTEGER) : agg,
        undefined as number | undefined,
      );
    },

    get lastPhase(): MindfulnessProgramTemplatePhase | undefined {
      return this.phases[this.phases.length - 1];
    },

    get totalUnits(): number {
      return this.phases.reduce((total, curr) => total + curr.totalUnits, 0);
    },

    get totalUnitsWithWeeks(): number {
      return this.phases.reduce((total, curr) => total + curr.totalUnitsWithWeeks, 0);
    },

    get timesOfDay(): TimeOfDay[] {
      return [
        ...new Set(
          this.phases.flatMap((phase) => phase.days.flatMap((day) => day.units.map((unit) => unit.timeOfDay))) ?? [],
        ),
      ];
    },

    toJS(newId: boolean = false) {
      const { id, createdAt, updatedAt, createdBy, updatedBy, name, description, ...rest } = base;
      return {
        id: newId ? UUID() : id,
        createdAt,
        updatedAt,
        createdBy,
        updatedBy,
        name,
        description,
        templateName: this.templateName,
        type: this.type,
        tags: toJS(this.tags),
        images: this.images.map((m) => m),
        phases: this.phases.map((p) => p.toJS(newId)),
        minDurationInMinutes: this.minDurationInMinutes,
        maxDurationInMinutes: this.maxDurationInMinutes,
        expectedResults: this.expectedResults.map((r) => r),
        quotes: this.quotes.map((q) => q),
        defaultTimesOfDay: Array.from(Object.entries(this.defaultTimesOfDay))
          .map(([key, value]) => [key, value])
          .reduce((agg, [key, entries]) => Object.assign(agg, { [key as string]: entries }), {}),
      };
    },

    save(): Promise<MindfulnessProgramTemplate> {
      return HttpBackend.post(`/coach/mindfulness/template`, this).then(() => this);
    },

    delete(): Promise<MindfulnessProgramTemplate> {
      return HttpBackend.delete(`/coach/mindfulness/template/admin/${this.id}`).then(() => this);
    },

    copy(): MindfulnessProgramTemplate {
      return createMindfulnessProgramTemplate(this.toJS(true));
    },

    getImage(tags: string[] = []): Media | undefined {
      const imageMedia = this.images.filter((m) => m.mediaType.startsWith('image'));
      // Try matching with tags first
      const tagged = imageMedia
        .filter((m) => m.matches(tags))
        .sort((a, b) => b.matchCount(tags) - a.matchCount(tags));
      if (tagged.length > 0) return tagged[0];
      // Fallback: any image regardless of tags
      if (imageMedia.length > 0) return imageMedia[0];
      // Last resort: first media of any type
      return this.images[0];
    },

    create(params?: Partial<CreateMindfulnessProgramRequest>): Promise<MicroCycle> {
      return HttpBackend.post(
        `/coach-program/mindfulness`,
        Object.assign(toJS(params ?? {}), { programTemplateId: this.id }),
      ).then((res) => createMicroCycle(res));
    },
  });

  return template;
}

export const MindfulnessProgramTemplateApi = {
  find(request?: Partial<MindfulnessProgramTemplateQueryRequest>): Promise<MindfulnessProgramTemplate[]> {
    return HttpBackend.get(`/coach-program/mindfulness/template`, toJS(request)).then((res) =>
      (res ?? []).map((r: any) => createMindfulnessProgramTemplate(r)),
    );
  },

  findOne(id: string): Promise<MindfulnessProgramTemplate> {
    return CacheService.cache(
      `mindfulnessProgramTemplate:${id}`,
      () => HttpBackend.get(`/coach-program/mindfulness/template/${id}`),
      (res) => createMindfulnessProgramTemplate(res),
      HOURS_1,
    ).then((res) => createMindfulnessProgramTemplate(res));
  },

  isActive(id: string): Promise<ProgramActiveResponse> {
    return HttpBackend.get(`/coach-program/mindfulness/template/${id}/isActive`).then((res) => createProgramActiveResponse(res));
  },

  count(request?: Partial<MindfulnessProgramTemplateQueryRequest>): Promise<number> {
    return HttpBackend.get(`/coach-program/mindfulness/template/count`, toJS(request)).then((res) => res ?? 0);
  },
};
